import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useUser } from '@/context/UserContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, 
  Trophy, 
  GraduationCap, 
  CheckCircle, 
  Lock,
  LogOut,
  Download,
  Loader2,
  Award,
  Calendar,
  Hash,
  Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSES = [
  { id: 'memeglish', name: 'MEMEGLISH', color: 'pump-green' },
  { id: 'memethematics', name: 'MEMETHEMATICS', color: 'degen-purple' },
  { id: 'memestory', name: 'MEMESTORY', color: 'yellow-500' },
  { id: 'memeart', name: 'MEMEART', color: 'cyan-400' }
];

const ProfilePage = () => {
  const { user, logout, refreshUser } = useUser();
  const [certificate, setCertificate] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user?.nft_certificate) {
      setCertificate(user.nft_certificate);
    }
  }, [user]);

  const getProgress = (courseId) => {
    return user?.progress?.[courseId] || { completed: false, quiz_passed: false };
  };

  const completedCourses = COURSES.filter(c => getProgress(c.id).quiz_passed).length;
  const allComplete = completedCourses === 4;

  const generateCertificate = async () => {
    if (!allComplete) {
      toast.error('Complete all courses first!');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/certificate/generate`, {
        user_id: user.id
      });
      setCertificate(response.data);
      await refreshUser();
      toast.success('Certificate generated! 🎓');
    } catch (error) {
      toast.error('Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const shareOnTwitter = () => {
    if (!certificate) return;
    const gpa = certificate.gpa?.toFixed(2);
    const siteUrl = window.location.origin;
    const detailUrl = `${siteUrl}/wall-of-traders/${certificate.id}`;
    const tweetText = `I just earned my Bachelor of Pumpology from Pump University with a ${gpa} GPA!\n\nThere's a trader in every retard, all you have to do is rearrange the letters.\n\nVerify my degree:\n${detailUrl}\n\n$PumpU`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <User className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="font-unbounded font-bold text-2xl text-white mb-2">Profile</h2>
          <p className="text-zinc-400">Enroll to view your profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 mb-8"
          data-testid="profile-header"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-pump-green/30 to-degen-purple/30 flex items-center justify-center">
              <span className="font-unbounded font-black text-4xl text-white">
                {user.username[0].toUpperCase()}
              </span>
            </div>
            <div className="text-center md:text-left flex-1">
              <h1 className="font-unbounded font-bold text-3xl text-white mb-1">
                {user.username}
              </h1>
              <p className="text-zinc-400">
                {allComplete ? 'Bachelor of Pumpology' : 'Student at Pump University'}
              </p>
              {allComplete && (
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                  <GraduationCap className="text-pump-green" size={18} />
                  <span className="text-pump-green text-sm font-medium">Certified Degen</span>
                </div>
              )}
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              className="text-zinc-400 hover:text-red-400"
              data-testid="logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </motion.div>

        {/* Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-8 mb-8"
          data-testid="progress-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="text-pump-green" size={24} />
            <h2 className="font-unbounded font-bold text-xl text-white">Course Progress</h2>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400">Overall Completion</span>
              <span className="text-pump-green font-mono">{completedCourses}/4 courses</span>
            </div>
            <Progress value={(completedCourses / 4) * 100} className="h-3 bg-pump-surface" />
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            {COURSES.map((course, idx) => {
              const progress = getProgress(course.id);
              const isComplete = progress.quiz_passed;
              const isLocked = idx > 0 && !getProgress(COURSES[idx - 1].id).quiz_passed;
              
              return (
                <div
                  key={course.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    isComplete 
                      ? 'bg-pump-green/10 border-pump-green/30' 
                      : isLocked
                        ? 'bg-pump-surface border-white/5 opacity-50'
                        : 'bg-pump-surface border-white/10'
                  }`}
                  data-testid={`course-progress-${course.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isComplete ? 'bg-pump-green/20' : 'bg-pump-card'
                  }`}>
                    {isComplete ? (
                      <CheckCircle className="text-pump-green" size={20} />
                    ) : isLocked ? (
                      <Lock className="text-zinc-600" size={18} />
                    ) : (
                      <span className="text-zinc-500 font-mono text-sm">{idx + 1}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${isComplete ? 'text-pump-green' : 'text-white'}`}>
                      {course.name}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {isComplete ? 'Completed' : isLocked ? 'Locked' : 'In Progress'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Certificate Section - Only show after graduation */}
        {allComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-8"
            data-testid="certificate-section"
          >
            <div className="flex items-center gap-3 mb-6">
              <Award className="text-degen-purple" size={24} />
              <h2 className="font-unbounded font-bold text-xl text-white">NFT Certificate</h2>
            </div>
          
          {certificate ? (
            <div className="relative">
              {/* Certificate Display with Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(57,255,20,0.15)] border border-pump-green/20">
                {/* Certificate Background Image */}
                <img 
                  src={certificate.certificate_image || "https://customer-assets.emergentagent.com/job_pumpology-edu/artifacts/4f3shcdi_Gemini_Generated_Image_9e95rc9e95rc9e95.png"}
                  alt="Bachelor of Pumpology Certificate"
                  className="w-full h-auto"
                />
                
                {/* Overlay Text - Name */}
                <div className="absolute inset-0 flex flex-col items-center pointer-events-none">
                  {/* Student Name - positioned on the name line */}
                  <div className="absolute w-full text-center" style={{ top: '40%' }}>
                    <p 
                      className="font-serif italic text-gray-800 px-4"
                      style={{ 
                        fontSize: 'clamp(16px, 4vw, 32px)',
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        letterSpacing: '2px',
                        fontWeight: '500'
                      }}
                    >
                      {certificate.student_name}
                    </p>
                  </div>
                  
                  {/* GPA - positioned above the GPA line */}
                  <div className="absolute w-full text-center" style={{ top: '54%' }}>
                    <p 
                      className="font-serif italic text-gray-800"
                      style={{ 
                        fontSize: 'clamp(14px, 3vw, 26px)',
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        letterSpacing: '1px',
                        fontWeight: '500'
                      }}
                    >
                      {certificate.gpa?.toFixed(2)}
                    </p>
                  </div>
                  
                  {/* Date - positioned after Date Awarded text */}
                  <div className="absolute" style={{ top: '63.5%', left: '58%' }}>
                    <p 
                      className="font-serif text-gray-700"
                      style={{ 
                        fontSize: 'clamp(10px, 1.8vw, 16px)',
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontStyle: 'italic'
                      }}
                    >
                      {new Date(certificate.issue_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Certificate Details Card */}
              <div className="mt-6 glass rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Certificate ID</span>
                  <span className="font-mono text-pump-green">{certificate.certificate_number}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">GPA</span>
                  <span className="font-bold text-xl text-white">{certificate.gpa?.toFixed(2)} / 4.00</span>
                </div>
                
                {/* Course Breakdown */}
                {certificate.course_grades && (
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm text-zinc-500 mb-3">Course Breakdown</p>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(certificate.course_grades).map(([course, grades]) => (
                        <div key={course} className="bg-pump-surface rounded-lg p-3">
                          <p className="text-xs text-zinc-500 uppercase">{course}</p>
                          <p className="text-white font-medium">{grades.score}/{grades.total} ({grades.percentage}%)</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* NFT Status */}
                <div className="pt-4 border-t border-white/10 flex items-center justify-center">
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    certificate.nft_status === 'minted' 
                      ? 'bg-pump-green/20 text-pump-green' 
                      : 'bg-degen-purple/20 text-degen-purple'
                  }`}>
                    {certificate.nft_status === 'minted' ? (
                      <>
                        <CheckCircle size={16} />
                        NFT Minted
                      </>
                    ) : (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Pending Mint
                      </>
                    )}
                  </span>
                </div>

                {/* Share on X/Twitter */}
                <div className="pt-4 border-t border-white/10">
                  <Button
                    onClick={shareOnTwitter}
                    className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 font-bold py-5 transition-all"
                    data-testid="share-twitter-btn"
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Share on X
                  </Button>
                </div>
              </div>
            </div>
          ) : allComplete ? (
            <div className="text-center py-8">
              <Award className="w-16 h-16 text-degen-purple mx-auto mb-4" />
              <h3 className="font-bold text-xl text-white mb-2">Ready to Graduate!</h3>
              <p className="text-zinc-400 mb-6">
                You've completed all courses. Generate your NFT certificate now!
              </p>
              <Button
                onClick={generateCertificate}
                disabled={generating}
                className="bg-pump-green text-black font-bold px-8 py-6 hover:shadow-neon-strong"
                data-testid="generate-certificate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Generating...
                  </>
                ) : (
                  <>
                    <GraduationCap className="mr-2" size={18} />
                    Generate NFT Certificate
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Lock className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-white mb-2">Certificate Locked</h3>
              <p className="text-zinc-400">
                Complete all 4 courses to unlock your Bachelor of Pumpology NFT certificate
              </p>
              <div className="mt-4">
                <Progress value={(completedCourses / 4) * 100} className="h-2 max-w-xs mx-auto bg-pump-surface" />
                <p className="text-sm text-zinc-500 mt-2">{completedCourses}/4 courses completed</p>
              </div>
            </div>
          )}
        </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
